import { SearchOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';

import dayjs from 'dayjs';
import { fetchArchivedPackingModeList, unArchivePacking } from '../../slices/packing';

export default function ArchivePacking() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [orderBy , setOrderBy] = useState('');
  const [pagesize, setPageSize] = useState(10);

  const dispatch = useDispatch();
  const { archiveData = [], totalRecord = 0, loading} = useSelector(state => state.packingMode);
  useEffect(() => {
    dispatch(
      fetchArchivedPackingModeList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy })
      }),
    );
  }, [dispatch, page, pagesize, search,orderBy]);

  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const resetFilter = () => {
    setSearch('');
    setPage(1);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'name' && sortOrder === 'ascend') {
      sortingEnumKey = 'name';
    }
    if (sortField === 'name' && sortOrder === 'descend') {
      sortingEnumKey = '-name';
    }

    return sortingEnumKey;
  };

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };
  const debounceTimeout = useRef(null);

  const handleSearch = (e) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 50);
  };
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const dataSource = archiveData?.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
  }));

  const handleRestore = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(unArchivePacking(id))
      .unwrap()
      .then(() => {
        message.success('Packing Mode Unarchived Successfully!');
        dispatch(
          fetchArchivedPackingModeList({
            page,
            pagesize,
            ...(search && { search: search }),
            ...(orderBy && { ordering: orderBy })
          }),
        );
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error); // Handle string error
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: false,
      render: (text) => (
        <div
          // WARNING: sanitize `text` if it comes from users/external sources
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ),
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? dayjs(record).format('DD-MM-YYYY HH:mm'): 'N/A'} </span>
        )
      }
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? `${record.first_name} ${record.last_name}` : 'N/A'} </span>
        )
      }
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'action',
      render: (text, record) => (
        <Space>
          <Link>
            <Tooltip title="Restore">
              <Popconfirm
                title="Are you sure you want to Unarchive this record?"
                onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
                onCancel={() => message.info('Action cancelled')} // Optional cancel action
                okText="Yes"
                cancelText="No"
              >
                <ArchiveRestore className="ArchiverestorImg" />
              </Popconfirm>
            </Tooltip>
          </Link>
        </Space>
      ),
    },
  ];

   const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  return (
    <>
    <div className='sticyaction'>
      <Row className="pagenamerow" justify="space-between" align="middle">
        <Col>
          <h2 style={{ margin: 0 }}>Archived Packing Modes</h2>
          <div className="bredcrumbwrp">
            <Link to="/packing-mode" className="back">
              <Back /> BACK
            </Link>
            <Breadcrumb
              items={[
                { title: <Link to="/packing-mode">Packing Modes</Link> },
                { title: 'Archived Packing Modes' },
              ]}
            />
          </div>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Search"
                  className='searchbox'
              prefix={<SearchOutlined />}
              value={search}
              style={{ width: 200 }}
              onChange={handleSearch}
            />
            <Link>
              <Tooltip placement="top" title={'Reset Filter'}>
                <Button
                  type="primary"
                  disabled={!search}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
            </Link>
          </Space>
        </Col>
      </Row>
      </div>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        // pagination={{
        //   defaultCurrent: page,
        //   defaultPageSize: pagesize,
        //   total: totalRecord,
        //   showTotal: (total) => `Total ${total} items`,
        //   showSizeChanger: true,
        //   onChange: handlePageChange,
        //   locale: {
        //     items_per_page: 'Items / Page', // Text for the size changer
        //   },
        // }}
        pagination={false}
        onChange={handleTableChange}
        scroll={{
          x: 880, // Horizontal Scroll
          y: 400,  // Vertical Scroll with Fixed Header
        }}
        style={{ marginTop: '20px' }}
      />
       </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>
    </>
  );
}
